import { useCallback } from "react";

import { useJitsu } from "@jitsu/react";
import { ErrorKind, useErrorCallback } from "@/data/error/useError";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { ResourceFragment, useUpdateResourceMutation } from "@/generated/graphql";

// Callback for updating an existing resource
type OnUpdateResource = (id: string, name: string, notes: string) => void;
type CallbackTuple = [OnUpdateResource, boolean];

export function useUpdateResource(
  resources: ResourceFragment[]
): CallbackTuple {
  const { track } = useJitsu();
  const onError = useErrorCallback(ErrorKind.EditTask);
  const [mutate, { loading }] = useUpdateResourceMutation({
    ignoreResults: true,
    onError,
    onCompleted: (data) => {
      track(JitsuEvent.RESOURCE_UPDATED, {
        resource_id: data.updateResource.id,
        resource_name: data.updateResource.name,
        resource_url: data.updateResource.url,
      });
    },
  });

  const updateResource = useCallback<OnUpdateResource>(
    (id, name, notes) => {
      const resource = resources.find((r) => r.id === id);

      if (resource) {
        mutate({
          variables: { id, name, notes, url: resource.url },
          optimisticResponse: {
            updateResource: {
              ...resource,
              name,
              invalidated: false,
              notes,
              __typename: "Resource",
            },
          },
        });
      }
    },
    [mutate, resources]
  );

  return [updateResource, loading];
}
